import React, { useContext, useEffect, useMemo, useState } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import ToolkitProvider, { Search } from "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit.min";
import { Icon } from "@iconify/react";
import { Link } from "react-router-dom";
import { ConfigContext } from "../context/GlobalContext";
import * as actionType from "../context/actions";
import PaginationView from "./PaginationView";
import { FilterdData } from "../helper/FilterdData";
import DataNotFoundView from "./DataNotFoundView";
import { useNavigate } from "react-router-dom";
import { Languages } from "../Utils/Languages";
import { Tooltip } from 'bootstrap/dist/js/bootstrap.bundle.min.js';
import CustomerDetailsPopup from "./child/CustomerDetailsPopup";
import { PremiumFeatureOnlyModal } from "./PremiumFeatureOnlyModal";
import { resolveAuditName } from "../Utils/audit_helper";
import BusinessDropdown from "./child/BusinessDropdown";
import Currancy from "../Utils/currency.json";


const CustomerTableDataLayer = ({ businessData, selectedBusiness: propSelectedBusiness, allAllowedIds, searchText = "" }) => {

    const navigate = useNavigate();

    const { state, dispatch } = useContext(ConfigContext);

    const { SearchBar } = Search;
    const [matchedCustomers, setMatchedCustomers] = useState([]);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [showPremiumModal, setShowPremiumModal] = useState(false);

    // ✅ Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(5);

    // ===========================
    // PLAN CONFIG
    // ===========================
    const starterPlanName = process.env.REACT_APP_STARTER_PLAN_NAME;
    const professionalPlanName = process.env.REACT_APP_PROFESSIONAL_PLAN_NAME;
    const agencyPlanName = process.env.REACT_APP_AGENCY_PLAN_NAME;

    const userPlanName = state?.PAYMENT?.[state?.PAYMENT?.length - 1]?.planName || "Free Plan";

    const isFreePlan = !state?.PREMIUM;
    const isStarterPlan = userPlanName === starterPlanName;
    const isProfessionalPlan = userPlanName === professionalPlanName;
    const isAgencyPlan = userPlanName === agencyPlanName;

    // ===========================
    // SORT BUSINESSES (Oldest → Newest)
    // ===========================
    const sortedBusinesses = useMemo(() => {
        return [...(state?.BUSINESSES || [])]
            .sort((a, b) => new Date(a.creationDate) - new Date(b.creationDate));
    }, [state?.BUSINESSES]);

    // ===========================
    // BUSINESS ACCESS LIMIT
    // ===========================
    let allowedBusinessCount = 1; // Free & Starter

    if (isProfessionalPlan) {
        allowedBusinessCount = 3;
    }

    if (isAgencyPlan) {
        allowedBusinessCount = sortedBusinesses.length;
    }

    // const allAllowedIds = useMemo(() => {
    //     const ownBusinesses = sortedBusinesses.filter(b => !b.isShared);
    //     const sharedBusinesses = sortedBusinesses.filter(b => b.isShared);

    //     const allowedOwnIds = ownBusinesses
    //         .slice(-allowedBusinessCount)
    //         .map(b => String(b.companyId));
    //     const sharedIds = sharedBusinesses.map(b => String(b.companyId));

    //     return [...allowedOwnIds, ...sharedIds];
    // }, [sortedBusinesses, allowedBusinessCount]);

    // ✅ Filter Data According to Selected Business & Currency
    useEffect(() => {
        const uniqueCustomers = FilterdData(state, state?.CUSTOMERS, "date");
        const filterCurrency = state?.CUSTOMER_CURRENCY_FILTER || "All";

        let customerList = [];

        if (propSelectedBusiness != "null" && propSelectedBusiness != null) {
            customerList = uniqueCustomers?.filter(customer => {
                const matchBiz = customer?.companyId == propSelectedBusiness;
                if (!matchBiz) return false;
                if (filterCurrency === "All") return true;

                const customerId = String(customer.customerId ?? customer.uniqueId ?? customer.id ?? "");
                const hasInvoiceWithCurrency = (state?.INVOICES || []).some(inv => 
                    String(inv.customerId) === customerId && 
                    (inv.currency || state?.CURRENCYCODE || "INR") === filterCurrency
                );
                const custCurrency = customer.currency || state?.CURRENCYCODE || "INR";
                return hasInvoiceWithCurrency || custCurrency === filterCurrency;
            });
        }
        else {
            customerList = uniqueCustomers?.filter(customer => {
                const matchBiz = allAllowedIds.includes(String(customer?.companyId));
                if (!matchBiz) return false;
                if (filterCurrency === "All") return true;

                const customerId = String(customer.customerId ?? customer.uniqueId ?? customer.id ?? "");
                const hasInvoiceWithCurrency = (state?.INVOICES || []).some(inv => 
                    String(inv.customerId) === customerId && 
                    (inv.currency || state?.CURRENCYCODE || "INR") === filterCurrency
                );
                const custCurrency = customer.currency || state?.CURRENCYCODE || "INR";
                return hasInvoiceWithCurrency || custCurrency === filterCurrency;
            });
        }

        const mappedList = customerList.map((customer, index) => ({
            ...customer,
            srNo: index + 1
        }));

        setMatchedCustomers(mappedList);

    }, [propSelectedBusiness, state?.ACTIVEFILTER, state?.CUSTOMERS, allAllowedIds, state?.CUSTOMER_CURRENCY_FILTER, state?.INVOICES, state?.CURRENCYCODE])

    // Compute amount of a single invoice (handles both with/without totalAmount)
    const computeInvoiceAmount = (inv) => {
        if (!inv) return 0;

        // 1) If already present
        const direct = inv.totalAmount ?? inv.total ?? inv.amount;
        if (direct != null) return Number(direct) || 0;

        // 2) Otherwise compute from items (+gstAmount - discountAmount)
        const itemsSum = Array.isArray(inv.items)
            ? inv.items.reduce((s, it) => {
                const price = Number(it.price || 0);
                const qty = Number(it.quantity || 0);
                return s + price * qty;
            }, 0)
            : 0;

        const gst = Number(inv.gstAmount || 0);
        const disc = Number(inv.discountAmount || 0);

        return itemsSum + gst - disc;
    };

    // Build a fast lookup: customerId -> total amount
    const useCustomerInvoiceTotalMap = (invoicesState, currencyFilter) =>
        React.useMemo(() => {
            const map = new Map();
            if (!invoicesState) return map;

            const processInvoice = (inv) => {
                const customerId = String(inv?.customerId ?? "");
                if (customerId) {
                    const invCurrency = inv.currency || state?.CURRENCYCODE || "INR";
                    if (currencyFilter && currencyFilter !== "All" && invCurrency !== currencyFilter) {
                        return;
                    }
                    const currentTotal = map.get(customerId) || 0;
                    map.set(customerId, currentTotal + computeInvoiceAmount(inv));
                }
            };

            if (Array.isArray(invoicesState)) {
                invoicesState.forEach(processInvoice);
            } else if (typeof invoicesState === "object") {
                Object.values(invoicesState).forEach(processInvoice);
            }
            return map;
        }, [invoicesState, currencyFilter]);

    const customerInvoiceTotalMap = useCustomerInvoiceTotalMap(state?.INVOICES, state?.CUSTOMER_CURRENCY_FILTER);




    // ✅ Pagination Logic
    const totalPages = Math.ceil(matchedCustomers?.length / itemsPerPage);
    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return matchedCustomers.slice(start, start + itemsPerPage);
    }, [matchedCustomers, currentPage, itemsPerPage]);

    // ✅ Sorting Icons
    const sortCaret = (order) => (
        <span className="d-inline-flex align-items-center ms-2">
            {!order ? (
                <Icon icon="mdi:swap-vertical" className="text-muted" />
            ) : order === "asc" ? (
                <Icon icon="mdi:arrow-up-thick" className="text-muted" />
            ) : (
                <Icon icon="mdi:arrow-down-thick" className="text-muted" />
            )}
        </span>
    );
    useEffect(() => {
        const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
        const tooltipList = Array.from(tooltipTriggerList).map(
            (tooltipTriggerEl) => new Tooltip(tooltipTriggerEl)
        );

        return () => tooltipList.forEach((tooltip) => tooltip.dispose());
    }, [paginatedData]);

    useEffect(() => {
        if (currentPage > totalPages && totalPages > 0) {
            setCurrentPage(totalPages);
        }
    }, [totalPages, currentPage]);
    // ✅ Table Columns
    const columns = [
        {
            dataField: "srNo",
            text: Languages[state?.LANGUAGE]?.s_r,
            headerAlign: "center",
            align: "center",
            sort: true,
            style: { minWidth: "80px" },
            sortCaret: sortCaret,
            events: {
                onClick: (e, column, columnIndex, row, rowIndex) => {
                    OnClickOfRow(row);
                }
            }
        },
        {
            dataField: "customer",
            text: Languages[state?.LANGUAGE]?.customer_name,
            sort: true,
            style: { minWidth: "150px" },
            sortCaret: sortCaret,
            formatter: (cell, row) => {
                const business = state?.BUSINESSES?.find(b => String(b.companyId) === String(row.companyId));
                const isSharedBusiness = business?.isShared === true;

                const currentUserId = state?.USERID;
                const currentUserStr = state?.SET_USER?.name || state?.SET_USER?.email || state?.SET_USER?.contact;

                const creatorName = row.createdBy
                    ? resolveAuditName(row.createdBy, state.membersData)
                    : null;

                const resolvedName = resolveAuditName(row.createdBy, state.membersData);

                const isCurrentUserCreator =
                    (typeof row.createdBy === 'object' && row.createdBy?.uid === currentUserId) ||
                    (typeof row.createdBy === 'string' && row.createdBy === currentUserId) ||
                    resolvedName === currentUserStr;

                // Only show 'by:' when someone else created it
                // OR it's a shared business with no createdBy (show owner name)
                let displayName = null;

                const showByLabel = row.createdBy
                    ? !isCurrentUserCreator
                    : (isSharedBusiness && business?.ownerName);

                if (showByLabel) {
                    displayName = row.createdBy
                        ? resolvedName
                        : business?.ownerName;
                }

                return (
                    <div style={{ marginLeft: -8, cursor: "pointer" }}>
                        <div>{cell || "Unknown"}</div>

                        {displayName && (
                            <small
                                className="text-secondary-light"
                                style={{ fontSize: '11px', display: 'block', marginTop: '-4px' }}
                            >
                                by: {displayName}
                            </small>
                        )}
                    </div>
                );
            },
            events: {
                onClick: (e, column, columnIndex, row, rowIndex) => {
                    OnClickOfRow(row);
                }
            }
        },
        {
            dataField: "phone",
            text: Languages[state?.LANGUAGE]?.phone,
            sort: true,
            style: { minWidth: "120px" },
            sortCaret: sortCaret,
            formatter: (cell, row) => (
                <span>{row?.dialCode}{cell}</span>
            ),
            events: {
                onClick: (e, column, columnIndex, row, rowIndex) => {
                    OnClickOfRow(row);
                }
            }
        },
        {
            dataField: "email",
            text: Languages[state?.LANGUAGE]?.email,
            sort: true,
            style: { minWidth: "180px" },
            sortCaret: sortCaret,
            events: {
                onClick: (e, column, columnIndex, row, rowIndex) => {
                    OnClickOfRow(row);
                }
            }
        },
        {
            dataField: "customerGSTNo",
            text: (state?.CURRENCYCODE === "AUD" || state?.TAXTYPE === "ABN") ? "ABN Number" : `${state?.TAXTYPE} Number`,
            sort: true,
            style: { minWidth: "150px" },
            sortCaret: sortCaret,
            events: {
                onClick: (e, column, columnIndex, row, rowIndex) => {
                    OnClickOfRow(row);
                }
            }
        },
        {
            dataField: "totalAmount",
            text: Languages[state?.LANGUAGE]?.total_amount, // or 'Outstanding Amount'
            sort: true,
            formatter: (cell, row) => {
                const customerId = String(row.customerId ?? row.uniqueId ?? row.id ?? "");
                const displayTotal = customerInvoiceTotalMap.get(customerId) || 0;

                // Calculate remaining (outstanding) amount
                const payments = Array.isArray(row.paymentsReceived) ? row.paymentsReceived : [];
                const filterCurrency = state?.CUSTOMER_CURRENCY_FILTER || "All";
                const filteredPayments = payments.filter(p => {
                    if (filterCurrency === "All") return true;
                    const paymentCurrency = p.currency || row.currency || state?.CURRENCYCODE || "INR";
                    return paymentCurrency === filterCurrency;
                });
                const totalPaidAmount = filteredPayments.reduce(
                    (sum, payment) => sum + (Number(payment?.amount) || 0),
                    0
                );

                const remainingAmount = displayTotal - totalPaidAmount;
                const amount = Number(remainingAmount || 0).toFixed(2); // ⭐ 2 decimal fix

                const activeCurrency = filterCurrency !== "All" ? filterCurrency : (row?.currency || state?.CURRENCYCODE || "INR");
                const matchedCurrency = activeCurrency ? Currancy.CountryCurrencyResponse.find(
                    (c) => c.currencyCode === activeCurrency
                ) : null;
                const currencyCode = matchedCurrency ? matchedCurrency.currencyCode : state?.CURRENCYCODE;
                const currencySymbol = matchedCurrency ? matchedCurrency.currencytSymbol : state?.SYMBOL;

                return (
                    <span>
                        {currencySymbol === "$"
                            ? `${currencyCode} ${currencySymbol} `
                            : `${currencySymbol} `}
                        {amount}
                    </span>
                );
            },
            sortCaret: sortCaret,
            events: { onClick: (e, c, ci, row) => OnClickOfRow(row) }
        },
        {
            dataField: "action",
            text: Languages[state?.LANGUAGE]?.action,
            headerAlign: "center",
            align: "center",
            formatter: (cell, row) => {
                return (
                    <>
                        {/* 👁 View */}
                        <Link
                            onClick={(e) => {
                                if (!state?.USERID) {
                                    e.preventDefault();
                                    dispatch({ type: actionType.GUESTMODELPOPUP, payload: true });
                                    return;
                                }
                                dispatch({ type: actionType.SELECTEDCUSTOMER, payload: row });
                            }}
                            to="/customer-view"
                            className="w-32-px h-32-px me-8 bg-primary-light text-primary-600 rounded-circle d-inline-flex align-items-center justify-content-center"
                            data-bs-toggle="tooltip"   // ✅ use standard Bootstrap tooltip
                            data-bs-placement="top"
                            data-bs-custom-class="tooltip-primary"
                            data-bs-title="View Customer"
                        >
                            <Icon icon="iconamoon:eye-light" />
                        </Link>


                        {/* ✏️ Edit */}
                        <Link
                            onClick={(e) => {
                                if (!state?.USERID) {
                                    e.preventDefault();
                                    dispatch({ type: actionType.GUESTMODELPOPUP, payload: true });
                                    return;
                                }
                                dispatch({ type: actionType.SELECTEDCUSTOMER, payload: row })
                            }}
                            to="/customer-edit"
                            className="w-32-px h-32-px me-8 bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                            data-bs-toggle="tooltip"   // ✅ use standard Bootstrap tooltip
                            data-bs-placement="top"
                            data-bs-custom-class="tooltip-success"
                            data-bs-title="Edit Customer"
                        >
                            <Icon icon="lucide:edit" />
                        </Link>

                        {/* 💵 Add Payment (3rd button) */}
                        <Link
                            to={(state?.PREMIUM || state?.BUSINESSES?.find(b => String(b.companyId) === String(row.companyId))?.isShared) ? "/customer-ledger" : "#"}
                            onClick={(e) => {
                                if (!state?.USERID) {
                                    e.preventDefault();
                                    dispatch({ type: actionType.GUESTMODELPOPUP, payload: true });
                                    return;
                                }
                                const business = state?.BUSINESSES?.find(b => String(b.companyId) === String(row.companyId));
                                const isShared = business?.isShared === true;

                                if (state?.PREMIUM || isShared) {
                                    // premium user or shared business member → allow navigation
                                    dispatch({ type: actionType.SELECTEDCUSTOMER, payload: row });
                                    setShowPremiumModal(false);
                                } else {
                                    // free user → stop navigation + show modal
                                    e.preventDefault();
                                    setShowPremiumModal(true);
                                }
                            }}
                            className="w-32-px h-32-px me-8 bg-warning-focus text-warning-main rounded-circle d-inline-flex align-items-center justify-content-center"
                            data-bs-toggle="tooltip"
                            data-bs-placement="top"
                            data-bs-custom-class="tooltip-warning"
                            data-bs-title="Ledger"
                        >
                            <Icon icon="mdi:book-open-page-variant" />
                        </Link>

                    </>
                );
            },
            style: { minWidth: "200px" }
        }

    ];

    const OnClickOfRow = (row) => {
        if (!state?.USERID) {
            dispatch({ type: actionType.GUESTMODELPOPUP, payload: true });
            return;
        }
        dispatch({ type: actionType.SELECTEDCUSTOMER, payload: row })
        navigate("/customer-view")
    }

    // ✅ Handle Change Items Per Page
    const handleSelection = (val) => {
        setItemsPerPage(val);
        setCurrentPage(1)
    };

    return (
        <div className="card">
            <div className="card-body">
                <ToolkitProvider
                    keyField="srNo"
                    data={matchedCustomers}
                    columns={columns}
                    search
                >
                    {props => {

                        // ✅ Filter data based on prop search from parent ListLayer header
                        const lowerSearch = searchText?.toLowerCase() || "";
                        const filteredData = matchedCustomers.filter(c =>
                            c.customer?.toLowerCase().includes(lowerSearch) ||
                            c.phone?.toLowerCase().includes(lowerSearch) ||
                            c.email?.toLowerCase().includes(lowerSearch) ||
                            c.customerGSTNo?.toLowerCase().includes(lowerSearch) ||
                            String(c.totalAmount).includes(lowerSearch)
                        );

                        // ✅ Pagination
                        const start = (currentPage - 1) * itemsPerPage;
                        const paginatedSearchData = filteredData.slice(start, start + itemsPerPage);

                        // ✅ Calculate total pages
                        const totalPages = Math.ceil(filteredData.length / itemsPerPage);

                        return (
                            <>

                                {/* Data Table */}
                                <div className="table-responsive">
                                    <BootstrapTable
                                        keyField="srNo"
                                        data={paginatedSearchData} // ✅ Use filtered + paginated data
                                        columns={columns}
                                        bootstrap4
                                        bordered={false}
                                        wrapperClasses={`custom-table-border invoice-table ${localStorage.getItem("theme") === "dark" ? "table-dark" : "table-light"}`}
                                        defaultSorted={[{ dataField: "srNo", order: "asc" }]}
                                        noDataIndication={() => <DataNotFoundView height={400} />}
                                        rowStyle={{ cursor: "pointer" }}
                                    />
                                </div>

                                {/* Pagination + Entries per Page */}
                                {filteredData?.length > 0 && (
                                    <div className="d-flex justify-content-between align-items-center mt-3">
                                        <div className="d-flex align-items-center gap-2 entry_per_page">
                                            <div className="dropdown">
                                                <button
                                                    className="btn btn-outline-primary-600 not-active px-18 py-6 dropdown-toggle toggle-icon"
                                                    type="button"
                                                    data-bs-toggle="dropdown"
                                                    aria-expanded="false"
                                                >
                                                    {itemsPerPage}
                                                </button>
                                                <ul className="dropdown-menu shadow-md">
                                                    {[5, 10, 15, 20].map(value => (
                                                        <li key={value}>
                                                            <Link
                                                                className={`dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 ${itemsPerPage == value ? "bg-primary-50" : ""
                                                                    }`}
                                                                onClick={() => handleSelection(value)}
                                                            >
                                                                {value}
                                                            </Link>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                            <span className="text-secondary-light">{Languages[state?.LANGUAGE]?.entries_per_page}</span>
                                        </div>
                                        <div className="ms-auto">
                                            <PaginationView
                                                currentPage={currentPage}
                                                totalPages={totalPages}
                                                handlePageChange={setCurrentPage}
                                            />
                                        </div>
                                    </div>
                                )}

                                <CustomerDetailsPopup
                                    show={showPaymentModal}
                                    onClose={() => setShowPaymentModal(false)}
                                    onSave={(customerId, record) => { }}
                                    customer={state?.SELECTEDCUSTOMER}
                                />

                                <PremiumFeatureOnlyModal
                                    isOpen={showPremiumModal}
                                    onClose={() => setShowPremiumModal(false)}
                                    featureName="Customer Ledger"
                                />
                            </>
                        );
                    }}
                </ToolkitProvider>

            </div>
        </div>
    );
};

export default CustomerTableDataLayer;
